import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FacebookShareButton } from 'react-share';
import { useMediaQuery } from 'react-responsive';
import ReactTooltip from 'react-tooltip';

import Button from '../Button';
import { IMAGE_SIZE_CODE } from '../../../constants/image-size-codes';
import DownloadPhotoButtonContainer from '../../../containers/global/DownloadPhotoButtonContainer';
import getConfig from '../../../util/config';
import FavoritesIcon from '../../icons/FavoritesIcon';
import CustomControl from '../CustomControl';
import DropdownMenu from '../DropdownMenu';
import DotTrigger from '../DotTrigger';
import { BASENAME } from '../../../constants/basenames';
import useBasename from '../../routing/useBasename';
import useOpenModal from '../../../hooks/useOpenModal';
import { SHARE_BLOCK_MODAL } from '../../../constants/modals';
import { NoContributorPermissionBlock } from '../../../apps/pro/services/contributors';
import useContributorPermission from '../../../apps/pro/services/contributors/hooks/useContributorPermission';
import SlideshowIcon from '../../icons/SlideshowIcon';
import CheckmarkIcon from '../../icons/CheckmarkIcon';
import { removeHashes } from '../../../util/text';
import PlicBooksSuccessIcon from '../../icons/PlicBooksSuccessIcon';
import { breakpointDown } from '../../../services/responsive/responsive';
import CoverPhotoButton from '../../../apps/pro/views/Album/AlbumPhotoViewer/CoverPhotoButton';
import FavoritesEmptyIcon from '../../icons/FavoritesEmptyIcon';
import {
  canAccessOriginalDownloadsOnBasename,
  canAccessForegroundUpdateOnBasename,
} from '../../../util/product-permissions';
import Angle from '../Angle';
import SpotlightIcon from '../../icons/SpotlightIcon';
import { PHOTO_FOREGROUND_UPDATE_MODAL } from '../PhotoForegroundUpdateModal';

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string,
    album: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  downloadEnabled: PropTypes.bool,
  shareActionEnabled: PropTypes.bool,
  coverPhotoActionEnabled: PropTypes.bool,
  isHighlighted: PropTypes.bool,
  isFavorite: PropTypes.bool,
  isHidden: PropTypes.bool,
  isReviewing: PropTypes.bool,
  onFavoritePhotoToggle: PropTypes.func,
  onHighlightToggle: PropTypes.func,
  onVisibilityToggle: PropTypes.func,
  className: PropTypes.string,
  dropdownClassName: PropTypes.string,
  dropdownContent: PropTypes.node,
  onDelete: PropTypes.func,
  withOverlay: PropTypes.bool,
  hasSlideshow: PropTypes.bool,
  onSlideshowToggle: PropTypes.func,
  onRemoveFaceHighlights: PropTypes.func,
  onCropPhoto: PropTypes.func,
  onFaceHighlightToggle: PropTypes.func,
  isFaceHighlight: PropTypes.bool,
};

// If displayUsername is set it is shown as the main name and the username - if set - comes in brackets second.
const AlbumPhotoMenu = ({
  photo,
  album,
  downloadEnabled = true,
  shareActionEnabled = true,
  coverPhotoActionEnabled = true,
  isHighlighted,
  isFavorite,
  isHidden,
  isReviewing,
  isSelected,
  selectable,
  onSelectToggle,
  onFavoritePhotoToggle,
  onHighlightToggle,
  onVisibilityToggle,
  className,
  dropdownClassName,
  dropdownContent,
  onDelete,
  withOverlay = true,
  hasSlideshow,
  onSlideshowToggle,
  onRemoveFaceHighlights,
  onCropPhoto,
  onViewPhotoUniverse,
  onFaceHighlightToggle,
  isFaceHighlight,
  ...props
}) => {
  const [downloadsToggled, setDownloadsToggled] = useState(false);
  const albumEntity = album ?? photo?.album;
  const basename = useBasename();
  const openModal = useOpenModal();
  const { isContributor } = useContributorPermission({
    entityId: albumEntity?.id,
  });
  const wrapperClases = classNames({
    'album-photo-menu-wrapper': true,
    selectable,
    selected: isSelected,
    [className]: className,
  });
  const dropdownClasses = classNames({
    'album-photo-menu-dropdown dropdown-menu-dark ml-2': true,
    [dropdownClassName]: dropdownClassName,
  });

  // No need to show the dropdown if none of these are available.
  const isMobile = useMediaQuery(breakpointDown('sm'));

  const basenamesWithFixedStar = [BASENAME.CAMPS, BASENAME.SCHOOLS];
  const shouldAlwaysIncludeStar = basenamesWithFixedStar.includes(basename);

  const showDropdown =
    downloadEnabled ||
    shareActionEnabled ||
    onVisibilityToggle ||
    onDelete ||
    (isMobile && (onFavoritePhotoToggle || hasSlideshow));

  return (
    <div className={wrapperClases} {...props}>
      {withOverlay && <div className="base-overlay" />}
      {selectable && (
        <CustomControl
          id={`checkbox-${photo.id}`}
          label={isSelected ? 'Photo selected' : 'Select this photo'}
          labelSrOnly
          type="checkbox"
          checked={isSelected}
          readOnly
          size="xxl"
          onClick={() => {
            onSelectToggle();
          }}
        />
      )}
      <div className="album-photo-menu-container">
        {shouldAlwaysIncludeStar && onFavoritePhotoToggle && !isMobile && (
          <Button
            btnStyle="reset"
            className={classNames({
              'album-photo-menu-button mr-auto': true,
              'is-favorite': isFavorite,
            })}
            onClick={onFavoritePhotoToggle}
          >
            {isFavorite ? (
              <FavoritesIcon color="#f8dc3e" />
            ) : (
              <FavoritesEmptyIcon />
            )}
          </Button>
        )}
        {onFaceHighlightToggle && (
          <Button
            btnStyle="reset"
            className={classNames({
              'album-photo-menu-button mr-auto': true,
              'is-spotlight': isFaceHighlight,
            })}
            onClick={onFaceHighlightToggle}
            data-for={`face-highlight-toggle-${photo.id}`}
            data-tip={
              isFaceHighlight ? 'Remove Spotlight' : 'Spotlight this photo'
            }
          >
            <SpotlightIcon color={isFaceHighlight ? '#0d84fe' : '#fff'} />
            <ReactTooltip
              id={`face-highlight-toggle-${photo.id}`}
              className="tooltip"
            />
          </Button>
        )}
        {!isHidden && albumEntity && hasSlideshow && !isMobile && (
          <NoContributorPermissionBlock entityId={albumEntity.id}>
            <Button
              btnStyle="reset"
              className={classNames({
                'album-photo-menu-button ': true,
                'slideshow-curation-button': !!photo.is_album_slideshow_photo,
              })}
              onClick={() => onSlideshowToggle(photo)}
            >
              {photo.is_album_slideshow_photo ? (
                <div className="checked-slideshow">
                  <SlideshowIcon color="#fff" />
                  <div>
                    <CheckmarkIcon size={7} className="slideshow-checkmark" />
                  </div>
                </div>
              ) : (
                <SlideshowIcon color="#fff" />
              )}
            </Button>
          </NoContributorPermissionBlock>
        )}
        {!isSelected && !selectable && showDropdown && (
          <div className={dropdownClasses}>
            <DropdownMenu
              toggle={<DotTrigger dotType="vertical" />}
              placement="bottom-end"
              closeOnMouseLeave={!isMobile}
            >
              {coverPhotoActionEnabled && (
                <CoverPhotoButton
                  btnStyle="reset"
                  className="dropdown-item"
                  photoId={photo?.id}
                  album={album}
                  skipQueries
                >
                  Use as cover photo
                </CoverPhotoButton>
              )}
              {onFavoritePhotoToggle &&
                (isMobile || !shouldAlwaysIncludeStar) && (
                  <Button
                    btnStyle="reset"
                    className="dropdown-item"
                    onClick={onFavoritePhotoToggle}
                  >
                    {isFavorite ? (
                      <>Remove from Favorites</>
                    ) : (
                      <>Add to Favorites</>
                    )}
                  </Button>
                )}
              {!isHidden && albumEntity && hasSlideshow && isMobile && (
                <Button
                  btnStyle="reset"
                  className="dropdown-item"
                  onClick={() => onSlideshowToggle(photo)}
                >
                  {photo.is_album_slideshow_photo ? (
                    <>Remove from Slideshow</>
                  ) : (
                    <>Add to Slideshow</>
                  )}
                </Button>
              )}
              {downloadEnabled && (
                <Fragment>
                  {canAccessOriginalDownloadsOnBasename(basename) ||
                  isContributor ? (
                    <div className="downloads-container">
                      <Button
                        btnStyle="reset"
                        className="dropdown-item"
                        onClick={() => setDownloadsToggled(prev => !prev)}
                      >
                        Download{' '}
                        <Angle
                          direction={downloadsToggled ? 'up' : 'down'}
                          className="ml-1"
                        />
                      </Button>
                      {downloadsToggled && (
                        <Fragment>
                          <DownloadPhotoButtonContainer
                            photoId={photo?.id}
                            photoSize={IMAGE_SIZE_CODE.LARGE2X}
                            includeWatermark={false}
                            btnStyle="reset"
                            btnSize="xs"
                            className="dropdown-item h-auto-i"
                            data-for={`${photo.id}-action-menu`}
                            data-tip="Download Hi-res"
                            data-place="left"
                          >
                            <p className="mb-0 text-left">
                              Download hi-res
                              <br />
                              <span className="text-light text-xs">
                                Great for prints up to 8x10
                              </span>
                            </p>
                          </DownloadPhotoButtonContainer>
                          <DownloadPhotoButtonContainer
                            photoId={photo?.id}
                            photoSize={IMAGE_SIZE_CODE.LARGE2X}
                            includeWatermark={false}
                            downloadOriginal
                            btnStyle="reset"
                            btnSize="xs"
                            className="dropdown-item h-auto-i"
                            data-for={`${photo.id}-action-menu-2`}
                            data-tip="Download Original"
                            data-place="left"
                          >
                            <p className="mb-0 text-left">
                              Download original
                              <br />
                              <span className="text-light text-xs">
                                Great for large format printing
                              </span>
                            </p>
                          </DownloadPhotoButtonContainer>
                          {photo?.foreground?.original_url && (
                            <DownloadPhotoButtonContainer
                              photoId={photo?.id}
                              photoSize={IMAGE_SIZE_CODE.LARGE2X}
                              includeWatermark={false}
                              downloadPhotoForeground
                              btnStyle="reset"
                              btnSize="xs"
                              className="dropdown-item h-auto-i"
                              data-for={`${photo.id}-action-menu-3`}
                              data-tip="Download Knock-out"
                              data-place="left"
                            >
                              <p className="mb-0 text-left">
                                Download knock-out
                                <br />
                                <span className="text-light text-xs">
                                  Great for custom backdrops
                                </span>
                              </p>
                            </DownloadPhotoButtonContainer>
                          )}
                        </Fragment>
                      )}
                    </div>
                  ) : (
                    <DownloadPhotoButtonContainer
                      photoId={photo?.id}
                      photoSize={IMAGE_SIZE_CODE.LARGE2X}
                      includeWatermark={false}
                      btnStyle="reset"
                      btnSize="xs"
                      className="dropdown-item"
                      data-for={`${photo.id}-action-menu`}
                      data-tip="Download"
                      data-place="left"
                    >
                      Download
                    </DownloadPhotoButtonContainer>
                  )}
                </Fragment>
              )}
              {photo?.foreground?.original_url &&
                canAccessForegroundUpdateOnBasename(basename) && (
                  <Button
                    btnStyle="reset"
                    className="dropdown-item"
                    onClick={() =>
                      openModal(PHOTO_FOREGROUND_UPDATE_MODAL, { photo })
                    }
                  >
                    Replace Knock-out
                  </Button>
                )}
              {shareActionEnabled && (
                <Fragment>
                  {photo.share_blocked ? (
                    <Button
                      btnStyle="reset"
                      className="dropdown-item"
                      onClick={() => openModal(SHARE_BLOCK_MODAL)}
                    >
                      Share on Facebook
                    </Button>
                  ) : (
                    <FacebookShareButton
                      className="btn-reset dropdown-item"
                      url={`${getConfig().app.baseUrl}/p/${photo?.id}`}
                      hashtag={
                        albumEntity?.hash_code &&
                        `#${removeHashes(albumEntity?.hash_code)}`
                      }
                    >
                      Share on Facebook
                    </FacebookShareButton>
                  )}
                </Fragment>
              )}
              {onVisibilityToggle && (
                <Button
                  btnStyle="reset"
                  className="dropdown-item"
                  onClick={onVisibilityToggle}
                >
                  {isHidden ? 'Publish Photo' : 'Hide Photo'}
                </Button>
              )}
              {onCropPhoto && (
                <Button
                  btnStyle="reset"
                  className="dropdown-item"
                  onClick={onCropPhoto}
                >
                  Adjust crop
                </Button>
              )}
              {onViewPhotoUniverse && (
                <Button
                  btnStyle="reset"
                  className="dropdown-item"
                  onClick={onViewPhotoUniverse}
                >
                  Locate photo in &apos;All&apos; view &rarr;
                </Button>
              )}
              {onDelete && (
                <Button
                  btnStyle="reset"
                  className="dropdown-item text-danger"
                  onClick={onDelete}
                >
                  Delete Photo
                </Button>
              )}
              {onRemoveFaceHighlights && (
                <Button
                  btnStyle="reset"
                  className="dropdown-item text-danger"
                  onClick={onRemoveFaceHighlights}
                >
                  Remove Spotlight
                </Button>
              )}
              {dropdownContent}
            </DropdownMenu>
          </div>
        )}
      </div>

      {photo?.album_oauth_link_photo_exists && (
        <div className="plic-branding">
          <PlicBooksSuccessIcon size={54} />
        </div>
      )}
    </div>
  );
};

AlbumPhotoMenu.propTypes = propTypes;

export default AlbumPhotoMenu;
